
import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

// Common interfaces
interface Margin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface ChartDimensions {
  width: number;
  height: number;
}

interface ChartData {
  name: string;
  value: number;
}

// PieChart props
export interface PieChartProps {
  data: ChartData[];
  width?: number;
  height?: number;
  colors?: string[];
  valueFormatter?: (value: number) => string;
  showLabels?: boolean;
  donut?: boolean;
  textColor?: string;
}

// D3PieChart Component
export const D3PieChart: React.FC<PieChartProps> = ({
  data,
  width = 400,
  height = 300,
  colors = d3.schemeCategory10,
  valueFormatter = (value) => `${value}`,
  showLabels = false,
  donut = false,
  textColor = '#333'
}) => {
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!ref.current || !data.length) return;

    const svg = d3.select(ref.current);
    const radius = Math.min(width, height) / 2;
    
    // Clear existing chart
    svg.selectAll('*').remove();

    // Create group for the pie chart
    const g = svg.append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    // Generate pie data
    const pie = d3.pie<ChartData>()
      .sort(null)
      .value(d => d.value);

    const arc = d3.arc<d3.PieArcDatum<ChartData>>()
      .innerRadius(donut ? radius * 0.5 : 0)
      .outerRadius(radius - 10);

    const labelArc = d3.arc<d3.PieArcDatum<ChartData>>()
      .innerRadius(radius * 0.5)
      .outerRadius(radius * 0.8);

    const colorScale = d3.scaleOrdinal<string>()
      .domain(data.map(d => d.name))
      .range(colors);

    // Draw pie slices
    const arcs = g.selectAll('.arc')
      .data(pie(data))
      .enter()
      .append('g')
      .attr('class', 'arc');

    arcs.append('path')
      .attr('d', d => arc(d) as string)
      .attr('fill', d => colorScale(d.data.name))
      .attr('stroke', 'white')
      .style('stroke-width', '2px')
      .style('opacity', 0.8);

    // Add labels if showLabels is true
    if (showLabels) {
      arcs.append('text')
        .attr('transform', d => `translate(${labelArc.centroid(d)})`)
        .attr('dy', '.35em')
        .style('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', textColor)
        .style('font-weight', 'bold')
        .text(d => d.data.value > 0 ? d.data.name : '');
    }

    // Add hover effects
    arcs.on('mouseover', function(event, d: d3.PieArcDatum<ChartData>) {
      d3.select(this).select('path')
        .style('opacity', 1);
        
      // Show tooltip with formatted value
      const tooltip = g.append("text")
        .attr("class", "tooltip")
        .attr("text-anchor", "middle")
        .attr("dy", "-1.2em")
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .style("fill", textColor)
        .text(`${d.data.name}: ${valueFormatter(d.data.value)}`);
    })
    .on('mouseout', function() {
      d3.select(this).select('path')
        .style('opacity', 0.8);
      g.select(".tooltip").remove();
    });

  }, [data, width, height, colors, valueFormatter, showLabels, donut, textColor]);

  return (
    <svg ref={ref} width={width} height={height} />
  );
};

// Bar Chart Props
export interface BarChartProps {
  data: ChartData[];
  width?: number;
  height?: number;
  margin?: Margin;
  color?: string;
  valueFormatter?: (value: number) => string;
  textColor?: string;
}

// D3BarChart Component
export const D3BarChart: React.FC<BarChartProps> = ({
  data,
  width = 500,
  height = 300,
  margin = { top: 20, right: 30, bottom: 40, left: 40 },
  color = "#8884d8",
  valueFormatter = (value) => `${value}`,
  textColor = "#333"
}) => {
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!ref.current || !data.length) return;

    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();
    
    // Calculate inner dimensions
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create the chart group
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create scales
    const x = d3.scaleBand()
      .domain(data.map(d => d.name))
      .range([0, innerWidth])
      .padding(0.2);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value) || 0])
      .nice()
      .range([innerHeight, 0]);

    // Add x-axis
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .style("fill", textColor);

    // Add y-axis
    g.append("g")
      .call(d3.axisLeft(y))
      .selectAll("text")
      .style("fill", textColor);

    // Add bars
    g.selectAll(".bar")
      .data(data)
      .enter().append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.name) as number)
      .attr("y", d => y(d.value))
      .attr("width", x.bandwidth())
      .attr("height", d => innerHeight - y(d.value))
      .attr("fill", color)
      .attr("opacity", 0.8);

    // Add hover effects and tooltip
    g.selectAll(".bar")
      .on("mouseover", function(event, d: ChartData) {
        d3.select(this)
          .attr("opacity", 1);
        
        g.append("text")
          .attr("class", "tooltip")
          .attr("x", x(d.name)! + x.bandwidth() / 2)
          .attr("y", y(d.value) - 10)
          .attr("text-anchor", "middle")
          .style("font-size", "12px")
          .style("font-weight", "bold")
          .style("fill", textColor)
          .text(valueFormatter(d.value));
      })
      .on("mouseout", function() {
        d3.select(this)
          .attr("opacity", 0.8);
        g.select(".tooltip").remove();
      });
  }, [data, width, height, margin, color, valueFormatter, textColor]);

  return <svg ref={ref} width={width} height={height} />;
};

// Line Chart Props
export interface LineChartProps {
  data: ChartData[];
  width?: number;
  height?: number;
  margin?: Margin;
  color?: string;
  valueFormatter?: (value: number) => string;
  areaFill?: boolean;
  textColor?: string;
}

// D3LineChart Component
export const D3LineChart: React.FC<LineChartProps> = ({
  data,
  width = 500,
  height = 300,
  margin = { top: 20, right: 30, bottom: 40, left: 40 },
  color = "#8884d8",
  valueFormatter = (value) => `${value}`,
  areaFill = false,
  textColor = "#333"
}) => {
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!ref.current || !data.length) return;

    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();
    
    // Calculate inner dimensions
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create the chart group
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create scales
    const x = d3.scaleBand()
      .domain(data.map(d => d.name))
      .range([0, innerWidth])
      .padding(0.2);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value) || 0])
      .nice()
      .range([innerHeight, 0]);

    // Add x-axis
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .style("fill", textColor);

    // Add y-axis
    g.append("g")
      .call(d3.axisLeft(y))
      .selectAll("text")
      .style("fill", textColor);

    // Create line generator
    const line = d3.line<ChartData>()
      .x(d => (x(d.name) || 0) + x.bandwidth() / 2)
      .y(d => y(d.value));

    // Add the line path
    g.append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", color)
      .attr("stroke-width", 2)
      .attr("d", line as any);

    // Add area if areaFill is true
    if (areaFill) {
      const area = d3.area<ChartData>()
        .x(d => (x(d.name) || 0) + x.bandwidth() / 2)
        .y0(innerHeight)
        .y1(d => y(d.value));

      g.append("path")
        .datum(data)
        .attr("fill", color)
        .attr("fill-opacity", 0.2)
        .attr("d", area as any);
    }

    // Add dots for each data point
    g.selectAll(".dot")
      .data(data)
      .enter().append("circle")
      .attr("class", "dot")
      .attr("cx", d => (x(d.name) || 0) + x.bandwidth() / 2)
      .attr("cy", d => y(d.value))
      .attr("r", 4)
      .attr("fill", color)
      .on("mouseover", function(event, d: ChartData) {
        d3.select(this).attr("r", 6);
        
        g.append("text")
          .attr("class", "tooltip")
          .attr("x", (x(d.name) || 0) + x.bandwidth() / 2)
          .attr("y", y(d.value) - 10)
          .attr("text-anchor", "middle")
          .style("font-size", "12px")
          .style("font-weight", "bold")
          .style("fill", textColor)
          .text(valueFormatter(d.value));
      })
      .on("mouseout", function() {
        d3.select(this).attr("r", 4);
        g.select(".tooltip").remove();
      });
  }, [data, width, height, margin, color, valueFormatter, areaFill, textColor]);

  return <svg ref={ref} width={width} height={height} />;
};

// Weekly Bar Chart Props
export interface WeeklyBarChartProps {
  data: Array<{ week: string, count: number }>;
  width?: number;
  height?: number;
  margin?: Margin;
  color?: string;
}

// D3WeeklyBarChart Component
export const D3WeeklyBarChart: React.FC<WeeklyBarChartProps> = ({
  data,
  width = 500,
  height = 300,
  margin = { top: 20, right: 30, bottom: 40, left: 40 },
  color = "#8884d8"
}) => {
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!ref.current || !data.length) return;

    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();
    
    // Calculate inner dimensions
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create the chart group
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create scales
    const x = d3.scaleBand()
      .domain(data.map(d => d.week))
      .range([0, innerWidth])
      .padding(0.2);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.count) || 0])
      .nice()
      .range([innerHeight, 0]);

    // Add x-axis
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)");

    // Add y-axis
    g.append("g")
      .call(d3.axisLeft(y));

    // Add bars
    g.selectAll(".bar")
      .data(data)
      .enter().append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.week) as number)
      .attr("y", d => y(d.count))
      .attr("width", x.bandwidth())
      .attr("height", d => innerHeight - y(d.count))
      .attr("fill", color);

    // Add hover effects and tooltip
    g.selectAll(".bar")
      .on("mouseover", function(event, d: { week: string, count: number }) {
        d3.select(this)
          .attr("opacity", 0.8);
        
        g.append("text")
          .attr("class", "tooltip")
          .attr("x", x(d.week)! + x.bandwidth() / 2)
          .attr("y", y(d.count) - 10)
          .attr("text-anchor", "middle")
          .style("font-size", "12px")
          .style("font-weight", "bold")
          .text(`${d.count} users`);
      })
      .on("mouseout", function() {
        d3.select(this)
          .attr("opacity", 1);
        g.select(".tooltip").remove();
      });
  }, [data, width, height, margin, color]);

  return <svg ref={ref} width={width} height={height} />;
};

// Responsive Container Props
interface ResponsiveContainerProps {
  width: string | number;
  height: string | number;
  children: React.ReactNode;
}

// D3ResponsiveContainer Component
export const D3ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
  width,
  height,
  children
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<ChartDimensions>({ width: 300, height: 200 });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setDimensions({
          width: typeof width === 'string' ? (parseFloat(width) / 100) * clientWidth : width,
          height: typeof height === 'string' ? (parseFloat(height) / 100) * clientHeight : height
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [width, height]);

  // Clone the child component with the new dimensions
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      // Type the props explicitly to fix the TypeScript error
      return React.cloneElement(child, {
        ...(dimensions as any) // Use type assertion to bypass TypeScript's strict checking
      });
    }
    return child;
  });

  return (
    <div ref={containerRef} style={{ width: width, height: height }}>
      {childrenWithProps}
    </div>
  );
};
